import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { FormikProvider, useFormik } from 'formik'
import moment from 'moment'

import {
  fetchSingleEmployee,
  POST_CREATE_EMPLOYEE_JOB_KEY,
  postCreateEmployeeJob,
  PUT_UPDATE_EMPLOYEE_JOB_KEY,
  PUT_UPDATE_JOB_COMPENSATION_KEY,
  putUpdateEmployeeJob,
  putUpdateJobCompensation,
} from '../payrollActions'
import {
  selectCompensationByEmployeeUuid,
  selectEmployeeByUuid,
  selectJobByEmployeeUuid,
} from '../payroll.selectors'
import { flsaDropdownOptions, useEmployeeWorkLocations } from '../helpers'
import { WorkAddressModal } from '../settings/WorkAddressModal'
import { PayrollError } from '../PayrollError'
import { Button, Card, Text, Link, Form, Separator } from '@/ui'
import { DATE_FORMATS } from '@/utils/dateHelpers'
import { useReselector } from '@/utils/sharedHooks'
import { useAppDispatch } from '@/utils/typeHelpers'
import { EmployeeWithoutJob } from '../employee.slice'
import { FlsaStatus, PaymentUnit } from '../employeeJob.slice'
import { TypographyStyles } from '@/styles'

// TODO: Migrate to V2
import {
  makeDateSchema,
  makeNumberSchema,
  makeReqStringSchema,
  FormikDateInput,
  getFieldNames,
} from '@/componentsV1/BaseComponents'
import { LabelDescription } from '@/componentsV1/BaseComponents/Input'

const VAL_END = 'for this employee'

const JobForm = ({ employee }: { employee: EmployeeWithoutJob }) => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const [modalOpen, setModalOpen] = useState(false)

  const { uuid } = employee

  const job = useReselector(selectJobByEmployeeUuid, uuid)
  const compensation = useReselector(selectCompensationByEmployeeUuid, uuid)

  const { locationOptions, setWorkLocation, defaultAddress } =
    useEmployeeWorkLocations(employee.uuid)

  const formik = useFormik({
    initialValues: {
      hire_date: job?.hire_date
        ? moment(job.hire_date, DATE_FORMATS.GUSTO_SUBMIT).format(
            DATE_FORMATS.INPUT
          )
        : '',
      title: job?.title || '',
      rate: job?.compensations[0]?.rate || '0',
      location_uuid: defaultAddress || '',
      flsa_status: compensation?.flsa_status || FlsaStatus.Exempt,
      payment_unit: compensation?.payment_unit || PaymentUnit.Year,
    },
    enableReinitialize: true,
    onSubmit: async ({
      hire_date,
      title,
      rate,
      payment_unit,
      flsa_status,
      location_uuid,
    }) => {
      const setWorkAddressSuccess = await setWorkLocation(location_uuid)

      if (!setWorkAddressSuccess) {
        return
      }

      let jobRes

      if (job?.uuid && job.version) {
        jobRes = await putUpdateEmployeeJob(job.uuid, {
          version: job.version,
          hire_date: moment(hire_date, DATE_FORMATS.INPUT).format(
            DATE_FORMATS.GUSTO_SUBMIT
          ),
          title,
        })(dispatch)
      } else {
        jobRes = await postCreateEmployeeJob(uuid, {
          hire_date: moment(hire_date, DATE_FORMATS.INPUT).format(
            DATE_FORMATS.GUSTO_SUBMIT
          ),
          title,
        })(dispatch)
      }

      const currentComp = jobRes?.compensations.find(
        (comp) => comp.uuid === jobRes?.current_compensation_uuid
      )

      if (!jobRes || !currentComp) {
        return
      }

      const compRes = await putUpdateJobCompensation(currentComp.uuid, {
        rate,
        payment_unit,
        flsa_status,
        version: currentComp.version,
      })(dispatch)

      if (compRes) {
        navigate(`/payroll/employee/${uuid}`)
      }
    },
  })

  const { isSubmitting, submitForm } = formik
  const fieldNames = getFieldNames(formik)

  return (
    <FormikProvider value={formik}>
      <div className="flex flex-col gap-6">
        <Text variant={TypographyStyles.H1}>Add a new team member</Text>

        <Text variant={TypographyStyles.H2}>Enter Employee Information</Text>

        <Separator />

        <PayrollError fetchKey={PUT_UPDATE_EMPLOYEE_JOB_KEY} />
        <PayrollError fetchKey={POST_CREATE_EMPLOYEE_JOB_KEY} />
        <PayrollError fetchKey={PUT_UPDATE_JOB_COMPENSATION_KEY} />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Form.Input
            label="Job Title"
            required
            name={fieldNames.title}
            schema={makeReqStringSchema({ field: 'job title', end: VAL_END })}
          />

          <Form.Select
            name={fieldNames.flsa_status}
            label="Employment Type"
            description={
              <Link
                href="https://support.joinheard.com/hc/en-us/articles/7479624872855"
                newPage
              >
                Help me with this
              </Link>
            }
            required
            options={flsaDropdownOptions}
            schema={makeReqStringSchema({
              field: 'employee type',
              end: VAL_END,
            })}
          />

          <FormikDateInput
            name={fieldNames.hire_date}
            label="Start Date"
            required
            schema={makeDateSchema({ field: 'start date' })}
          />
        </div>

        <LabelDescription label="Compensation" required />

        <div className="grid grid-cols-1 md:grid-cols-12 items-center gap-4">
          <div className="md:col-span-6">
            <Form.Input
              name={fieldNames.rate}
              componentType="currency"
              schema={makeNumberSchema({
                field: 'rate',
                allowedDecimals: 2,
                end: VAL_END,
              })}
            />
          </div>
          <div className="md:col-span-1">
            <Text>Per</Text>
          </div>
          <div className="md:col-span-5">
            <Form.Select
              name={fieldNames.payment_unit}
              options={Object.values(PaymentUnit)
                .filter((name) => name !== 'Paycheck')
                .map((name) => ({
                  value: name,
                  text: name,
                }))}
              schema={makeReqStringSchema({
                field: 'payment unit',
                end: VAL_END,
              })}
            />
          </div>
        </div>

        <Form.Select
          name={fieldNames.location_uuid}
          options={locationOptions}
          label="Work Address"
          required
          description="This is where the employee primarily works."
        />

        <Text>
          Location missing?{' '}
          <Button variant="link" onClick={() => setModalOpen(true)}>
            Add work address here
          </Button>
        </Text>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 mt-4">
          <div className="md:col-span-6">
            <Button
              variant="secondary"
              onClick={() => navigate(`/payroll/employee/${uuid}`)}
              className="w-full"
            >
              Cancel
            </Button>
          </div>
          <div className="md:col-span-4" />
          <div className="md:col-span-6">
            <Button
              onClick={submitForm}
              disabled={isSubmitting}
              className="w-full"
            >
              Save
            </Button>
          </div>
        </div>
      </div>

      {modalOpen && (
        <WorkAddressModal
          open={modalOpen}
          closeModal={() => setModalOpen(false)}
        />
      )}
    </FormikProvider>
  )
}

export const CreateEmployeeJob = () => {
  const dispatch = useAppDispatch()
  const { uuid } = useParams()
  const employee = useReselector(selectEmployeeByUuid, uuid)

  useEffect(() => {
    if (uuid) {
      fetchSingleEmployee(uuid)(dispatch)
    }
  }, [dispatch, uuid])

  return <Card>{employee && <JobForm employee={employee} />}</Card>
}
